import {useCallback, useMemo, useState} from 'react';
import {handleFetchError, openNewTab, randomString, setWaitingInterval, showFlashMessage, translate} from '../../utils';
import {emailSendModal, reservationSearchModal, stripeReaderSessionModal} from '../../modals';
import {startLoadingIndicator, stopLoadingIndicator} from '../../loading';
import {ReservationPanel} from './ReservationPanel';
import {InvoiceDraftPanel} from './InvoiceDraftPanel';
import {ItemsCollection} from './ItemsCollection';
import {createPortal} from 'react-dom';
import {useBroadcastChannel} from '../../hooks/useBroadcashChannel';
import {HISTORY_EMAIL_TYPE_INVOICE} from '../../constants';
import {usePersistQueue} from '../../hooks/usePersistQueue';

// In some concurrency cases the limit might be exceeded - thats fine.
const INVOICE_DRAFTS_SOFT_LIMIT = 10;
const createInvoiceActionHandledServerError = Symbol('createInvoiceActionHandledServerError');

export function ReservationInvoiceDraft({defaultReservation, defaultInvoiceDrafts, paymentMethods}) {
    const [reservation, setReservation] = useState(defaultReservation);
    const [invoiceDrafts, setInvoiceDrafts] = useState(() => {
        if (defaultInvoiceDrafts.length > 0) {
            const invoiceDrafts = [];
            if (reservation.reservationRooms.length || reservation.services.length || reservation.deposits.length) {
                // As long as there's any unallocated items, always have an empty draft available for convenience.
                invoiceDrafts.push(
                    defaultInvoiceDraft({paymentMethods, defaultReservation}),
                );
            }

            return invoiceDrafts.concat(
                defaultInvoiceDrafts.map(invoiceDraft => ({
                    id: invoiceDraft.id,
                    apiId: invoiceDraft.id,
                    recipient: [invoiceDraft.client, invoiceDraft.contact],
                    invoice: null,
                    paymentMethodAmounts: invoiceDraft.paymentMethodAmounts,
                    collection: collectionFromModel(invoiceDraft),
                    alerts: [],
                    eInvoice: invoiceDraft.eInvoice,
                })),
            );
        } else {
            return [defaultInvoiceDraft({paymentMethods, defaultReservation})];
        }
    });

    const [unallocatedCollection, setUnallocatedCollection] = useState(() => collectionFromModel(defaultReservation));
    const [alreadyInvoicedItems, setAlreadyInvoicedItems] = useState(() => ({
        reservationRooms: new Set(),
        services: new Set(),
        deposits: new Set(),
    }));

    // A draft will be persisted on the first save. However, if there's immediately a 2nd change, before the 1st persist has gone through, we dont know what id to use for saving, and we cant send an empty one either, as that will result in a duplicate draft saved. To avoid this, queue up all changes for the same invoice draft.
    const queueAction = usePersistQueue();
    const persistInvoiceDraftAction = useCallback((invoiceDraft, collection, recipient, paymentMethodAction) => {
        queueAction(invoiceDraft.id, (apiId) => {
            return persistInvoiceDraft(defaultReservation.id, apiId, collection, recipient, paymentMethodAction).then(response => {
                if (response.ok) {
                    return response.json();
                }
            }).then(responseBody => {
                if (responseBody) {
                    setInvoiceDrafts(state => {
                        const index = state.findIndex(value => value.id === invoiceDraft.id);
                        if (index < 0) {
                            return state;
                        }

                        const nextState = [...state];
                        nextState[index] = {...nextState[index], apiId: responseBody.id};
                        if (responseBody.eInvoice) {
                            nextState[index].eInvoice = responseBody.eInvoice;
                        } else {
                            delete nextState[index].eInvoice;
                        }
                        return nextState;
                    });
                }

                // In case of a server error, do the next persist with the id we already have. This allows handling temporary issues more gracefully
                return responseBody?.id || invoiceDraft.apiId;
            });
        }, invoiceDraft.apiId);
    }, [defaultReservation, queueAction]);

    const setInvoiceDraft = useCallback((invoiceDraft, values) => {
        // Changes in items might lead to inconsistent payment method amounts
        if ('collection' in values && invoiceDraft.paymentMethodAmounts.some(paymentMethodAmount => paymentMethodAmount.amount !== null)) {
            const total = values.collection.total;
            if (total <= 0.0) {
                // Only positive amounts can be split
                values.paymentMethodAmounts = [
                    {paymentMethod: invoiceDraft.paymentMethodAmounts[0].paymentMethod, amount: null},
                    {paymentMethod: null, amount: null},
                    {paymentMethod: null, amount: null},
                ];
            } else if (invoiceDraft.paymentMethodAmounts.reduce((carry, paymentMethodAmount) => carry + paymentMethodAmount.amount, 0.0) !== total && invoiceDraft.paymentMethodAmounts.filter(paymentMethodAmount => paymentMethodAmount.amount !== 0.0).length <= 1) {
                // For convenience, adjust the total *only* when it can be done without being annoying.
                values.paymentMethodAmounts = [...values.paymentMethodAmounts];
                const index = Math.min(0, invoiceDraft.paymentMethodAmounts.findIndex(paymentMethodAmount => paymentMethodAmount.amount !== 0.0));
                values.paymentMethodAmounts = [...invoiceDraft.paymentMethodAmounts];
                values.paymentMethodAmounts[index] = {...values.paymentMethodAmounts[index], amount: total}
            }
        }

        setInvoiceDrafts(state => {
            const index = state.findIndex(value => value.id === invoiceDraft.id);
            const nextState = [...state];
            nextState[index] = {...state[index], ...values};
            if (state[index].alerts.length > 0 && !('alerts' in values)) {
                // Assume that any modification is an attempt by the user to resolve any error.
                nextState[index].alerts = [];
            }

            return nextState;
        });

        if ('recipient' in values || 'paymentMethodAmounts' in values || 'collection' in values) {
            persistInvoiceDraftAction(invoiceDraft, values.collection || invoiceDraft.collection, values.recipient || invoiceDraft.recipient, values.paymentMethodAmounts || invoiceDraft.paymentMethodAmounts);
        }
    }, [persistInvoiceDraftAction]);

    useBroadcastChannel('invoice_preview_finalize', (event) => {
        const invoiceDraftId = invoiceDrafts.findIndex(invoiceDraft => {
            return invoiceDraft.collection.items.length > 0 && invoiceDraft.collection.items.every(([type, item]) => (event.data.items)[type + 's'] && (event.data.items)[type + 's'].includes(item.id));
        });

        if (invoiceDraftId !== -1 && !invoiceDrafts[invoiceDraftId].invoice) {
            setInvoiceDraft(invoiceDrafts[invoiceDraftId], {
                invoice: {
                    id: event.data.id,
                    key: event.data.key,
                    pdf: event.data.pdf,
                },
            });
        }
    });

    useBroadcastChannel('invoice_preview_stripe_abort', (event) => {
        setUnallocatedCollection(state => state.union(ItemsCollection.fromUnordered(
            event.data.deposits.map((deposit) => ['deposit', deposit]),
        )));
    });

    const searchReservation = useCallback(() => {
        reservationSearchModal((reservation) => {
            startLoadingIndicator();
            loadReservation(reservation.id).then(response => {
                setReservation(response);
                setUnallocatedCollection(collectionFromModel(response));
                stopLoadingIndicator();
            });
        }, {not: {id: {eq: reservation.id}}});
    }, [reservation]);

    const addInvoiceDraft = useCallback(() => {
        const invoiceDraft = defaultInvoiceDraft({paymentMethods, defaultReservation});
        setInvoiceDrafts(state => [...state, invoiceDraft]);
    }, [paymentMethods, defaultReservation]);

    const removeInvoiceDraftAction = useCallback((invoiceDraft) => {
        setInvoiceDrafts(state => state.filter(value => value.id !== invoiceDraft.id));
        setUnallocatedCollection(state => state.union(invoiceDraft.collection));

        queueAction(invoiceDraft.id, (apiId) => {
            // Persisting might have failed, or resulted in an already-deleted invoice draft.
            if (apiId) {
                return removeInvoiceDraft(apiId).then(() => null);
            } else {
                return Promise.resolve(null);
            }
        }, invoiceDraft.apiId);
    }, [queueAction]);

    const toggleSelection = useCallback((collection, selectedItem) => {
        setUnallocatedCollection(state => {
            if (state === collection) {
                return state.select(item => {
                    const isSelected = state.isSelected(item[1]);
                    return item[1] === selectedItem ? !isSelected : isSelected;
                });
            } else {
                return state;
            }
        });

        setInvoiceDrafts(state => {
            const index = state.findIndex(value => value.collection === collection);
            if (index < 0) {
                // Selected item may be unallocated, in which case no invoiceDraft state changes.
                return state;
            }

            const nextState = [...state];
            nextState[index] = {
                ...nextState[index],
                collection: collection.select(item => {
                    const isSelected = collection.isSelected(item[1]);
                    return item[1] === selectedItem ? !isSelected : isSelected;
                }),
            };
            return nextState;
        });
    }, []);

    const addItems = useCallback((invoiceDraft, predicate) => {
        const [movedCollection, newUnallocatedCollection] = unallocatedCollection.partition(predicate);
        if (movedCollection.items.length === 0) {
            // nothing to move
            return movedCollection;
        }

        const newCollection = invoiceDraft.collection.union(movedCollection.select(() => false));

        setUnallocatedCollection(newUnallocatedCollection);
        setInvoiceDraft(invoiceDraft, {collection: newCollection});

        return movedCollection;
    }, [setInvoiceDraft, unallocatedCollection]);

    const removeItems = useCallback((invoiceDraft, predicate) => {
        const [movedCollection, newCollection] = invoiceDraft.collection.partition(predicate);
        if (movedCollection.items.length === 0) {
            // nothing to move
            return movedCollection;
        }

        const newUnallocatedCollection = unallocatedCollection.union(movedCollection.select(() => false));

        setUnallocatedCollection(newUnallocatedCollection);
        setInvoiceDraft(invoiceDraft, {collection: newCollection});

        return movedCollection;
    }, [setInvoiceDraft, unallocatedCollection]);

    function clearAll() {
        for (const invoiceDraft of invoiceDrafts) {
            if (invoiceDraft.invoice) {
                // Invoiced panels cannot be removed
                continue;
            }
            removeInvoiceDraftAction(invoiceDraft);
        }

        // Show at least one panel.
        if (!invoiceDrafts.some(invoiceDraft => invoiceDraft.invoice)) {
            const emptyInvoiceDraft = defaultInvoiceDraft({paymentMethods, defaultReservation});
            setInvoiceDrafts([emptyInvoiceDraft]);
        }
    }

    const previewAction = useCallback(async (invoiceDraft) => {
        const tabOpened = await openNewTab(
            previewInvoice(reservation, invoiceDraft.collection, invoiceDraft.recipient, invoiceDraft.paymentMethodAmounts).then(response => response.ok ? response.text() : null),
        );
        if (!tabOpened) {
            alert(translate('frontend', 'ERROR_INPROCESS'));
        }
    }, [reservation]);

    const createInvoiceAction = useCallback((invoiceDraft) => {
        let readerPromise;
        if (window.config.stripe.reader_payment_method && invoiceDraft.paymentMethodAmounts.some(({paymentMethod}) => paymentMethod === window.config.stripe.reader_payment_method) && invoiceDraft.collection.total !== 0.0) {
            const stripePaymentMethodAmounts = [];
            const stripePaymentMethodAmountsMap = new Map();
            for (let index in invoiceDraft.paymentMethodAmounts) {
                const paymentMethodAmount = invoiceDraft.paymentMethodAmounts[index];
                if (paymentMethodAmount.paymentMethod === window.config.stripe.reader_payment_method) {
                    stripePaymentMethodAmounts.push(paymentMethodAmount.amount ?? invoiceDraft.collection.total);
                    stripePaymentMethodAmountsMap.set(stripePaymentMethodAmountsMap.size, index);
                }
            }

            readerPromise = stripeReaderSessionModal(reservation.id, stripePaymentMethodAmounts).then(({action, readerSessions}) => {
                if (action === 'finalize' && readerSessions.every(readerSession => readerSession.status === 'succeeded')) {
                    const paymentIntents = [];
                    for (let index in readerSessions) {
                        paymentIntents[stripePaymentMethodAmountsMap.get(Number(index))] = readerSessions[index].paymentIntentId;
                    }
                    return [true, paymentIntents];
                } else if (readerSessions.some(readerSession => readerSession.paymentIntentId)) {
                    startLoadingIndicator();

                    return Promise
                        .all(readerSessions.map(readerSession => readerSession.abort()))
                        .then(results => {
                            setUnallocatedCollection(state => state.union(ItemsCollection.fromUnordered(
                                results
                                    .filter((result) => result.deposit)
                                    .map((result) => ['deposit', result.deposit]),
                            )));

                            return [false, []];
                        })
                        .finally(() => {
                            stopLoadingIndicator();
                        });
                } else {
                    return [false, []];
                }
            });
        } else {
            readerPromise = Promise.resolve([true, []]);
        }

        return readerPromise.then(([paid, paymentIntents]) => {
            if (!paid) {
                return null;
            }

            startLoadingIndicator();
            return finalizeInvoice(invoiceDraft.collection, invoiceDraft.recipient, invoiceDraft.paymentMethodAmounts, paymentIntents).then(async (response) => {
                if (response.ok || response.status === 422 || response.status === 409) {
                    const body = await response.json();
                    if (response.ok) {
                        setInvoiceDraft(invoiceDraft, {
                            invoice: {
                                id: body.invoice_id,
                                key: body.invoice_key,
                                pdf: body.message,
                            },
                        });

                        return body;
                    } else if (response.status === 422) {
                        setAlreadyInvoicedItems(state => {
                            return {
                                reservationRooms: new Set([...state.reservationRooms, ...body.rooms || []]),
                                services: new Set([...state.services, ...body.products || []]),
                                deposits: new Set([...state.deposits, ...body.deposits || []]),
                            };
                        });

                        return createInvoiceActionHandledServerError;
                    } else if (response.status === 409) {
                        setInvoiceDraft(invoiceDraft, {
                            alerts: [body.message],
                        });

                        return createInvoiceActionHandledServerError;
                    }
                }

                return false;
            }).finally(() => {
                stopLoadingIndicator();
            })
        });
    }, [reservation, setInvoiceDraft]);

    const finalizeAction = useCallback(async (invoiceDraft) => {
        if (invoiceDraft.invoice) {
            await openNewTab(invoiceDraft.invoice.pdf);
        } else {
            const body = await createInvoiceAction(invoiceDraft);

            if (body === null) {
                // payment was cancelled
            } else if (body === createInvoiceActionHandledServerError) {
                // Errors for status 422 and 409 are handled via inline alerts.
            } else if (body === false) {
                alert(translate('frontend', 'ERROR_INPROCESS'));
            } else {
                await openNewTab(body.message);
            }
        }
    }, [createInvoiceAction]);

    const sendAction = useCallback(async (invoiceDraft) => {
        if (invoiceDraft.invoice) {
            await tryOpenMailPopup(invoiceDraft.invoice.key);
        } else {
            const body = await createInvoiceAction(invoiceDraft);

            if (body === null) {
                // payment was cancelled
            } else if (body === createInvoiceActionHandledServerError) {
                // Errors for status 422 and 409 are handled via inline alerts.
            } else if (body === false) {
                alert(translate('frontend', 'ERROR_INPROCESS'));
            } else {
                await tryOpenMailPopup(body.invoice_key);
            }
        }
    }, [createInvoiceAction]);

    const pageActionContainer = useMemo(() => document.getElementById('pageActions'), []);

    // Drafts with no items dont get saved anyway, so any other non-default changes are not relevant.
    const hasNonEmptyDrafts = invoiceDrafts.some(invoiceDraft => invoiceDraft.collection.items.length > 0);

    return <div className={'row'}>
        {hasNonEmptyDrafts
            ? createPortal(<button type="button" className="btn btn-default-clear" onClick={clearAll}>
                <i className="fa fa-trash"></i> {translate('frontend', 'INVOICE_DRAFT_CLEAR_ALL_ACTION')}
            </button>, pageActionContainer)
            : null
        }
        <div className="col-md-6">
            <ReservationPanel
                reservationCode={reservation.code}
                alreadyInvoicedItems={alreadyInvoicedItems}
                collection={unallocatedCollection}
                toggleSelection={toggleSelection}
                searchReservation={searchReservation}
            />
        </div>
        <div className="col-md-6 vstack gap-2">
            {invoiceDrafts.map(invoiceDraft => {
                return <InvoiceDraftPanel
                    key={invoiceDraft.id}
                    invoiceDraft={invoiceDraft}
                    setInvoiceDraft={setInvoiceDraft}
                    reservationKey={reservation.id}
                    paymentMethods={paymentMethods}
                    remove={removeInvoiceDraftAction}
                    alreadyInvoicedItems={alreadyInvoicedItems}
                    addItems={unallocatedCollection.items.length ? addItems : null}
                    removeItems={invoiceDraft.collection.items.length ? removeItems : null}
                    toggleSelection={toggleSelection}
                    previewAction={previewAction}
                    finalizeAction={finalizeAction}
                    sendAction={sendAction}
                />;
            })}
            {invoiceDrafts.length < INVOICE_DRAFTS_SOFT_LIMIT ? <div className="row">
                <div className="col-xs-10 col-xs-offset-1">
                    <button type="button" className="btn btn-default-clear" onClick={addInvoiceDraft}>
                        <i className="fa fa-plus"/> {translate('frontend', 'New invoice')}
                    </button>
                </div>
            </div> : null}
        </div>
    </div>;
}

function defaultInvoiceDraft({paymentMethods, defaultReservation}) {
    const defaultPaymentMethod = paymentMethods.find(({id}) => id === window.config.stripe.reader_payment_method)?.id || null;

    return {
        id: '__' + randomString(8),
        apiId: null, // A stable identity value is needed, even when apiId is assigned or changes.
        recipient: [defaultReservation.client, defaultReservation.contact],
        invoice: null,
        paymentMethodAmounts: [
            {paymentMethod: defaultPaymentMethod, amount: null},
            {paymentMethod: null, amount: null},
            {paymentMethod: null, amount: null},
        ],
        collection: ItemsCollection.fromUnordered([]),
        alerts: [],
        eInvoice: null,
    };
}

function collectionFromModel(model) {
    const items = [];
    for (const reservationRoom of model.reservationRooms) {
        items.push(['reservationRoom', reservationRoom]);
    }
    for (const service of model.services) {
        items.push(['service', service]);
    }
    for (const deposit of model.deposits) {
        items.push(['deposit', deposit]);
    }

    return ItemsCollection.fromUnordered(items);
}

function loadReservation(reservationKey) {
    return fetch(`/invoice-draft/get-reservation?key=${reservationKey}`, {
        method: 'GET',
    }).then(r => r.json()).catch(handleFetchError);
}

function removeInvoiceDraft(invoiceDraftId) {
    const body = new FormData();
    body.append('invoice_draft', invoiceDraftId);

    return fetch(`/invoice-draft/delete`, {
        method: 'POST',
        body,
    });
}

async function previewInvoice(reservation, collection, recipient, paymentMethodAmounts) {
    const body = prepareInvoiceFormData(collection, recipient, paymentMethodAmounts);
    body.append('reservation', reservation.id);

    return fetch('/invoice/splitinvoice_preview', {
        method: 'POST',
        body: body,
    });
}

async function finalizeInvoice(collection, recipient, paymentMethodAmounts, paymentIntents) {
    const body = prepareInvoiceFormData(collection, recipient, paymentMethodAmounts, paymentIntents);
    body.append('actType', '1');

    return fetch('/invoice/final_invoice_save_print_preview', {
        method: 'POST',
        body,
    });
}

function persistInvoiceDraft(reservationId, invoiceDraftId, collection, recipient, paymentMethodAmounts) {
    const body = prepareInvoiceFormData(collection, recipient, paymentMethodAmounts);
    body.append('reservation', reservationId);
    if (invoiceDraftId) {
        body.append('invoice_draft', invoiceDraftId);
    }

    return fetch(`/invoice-draft/persist`, {
        method: 'POST',
        body,
    });
}

/**
 * @param {ItemsCollection} collection
 * @return {FormData}
 */
function prepareInvoiceFormData(collection, recipient, paymentMethodAmounts, paymentIntents = []) {
    const data = new FormData();
    data.append('rooms', JSON.stringify(collection.items.filter(item => item[0] === 'reservationRoom').map(item => item[1].id)));
    data.append('products', JSON.stringify(collection.items.filter(item => item[0] === 'service').map(item => item[1].id)));
    data.append('deposits', JSON.stringify(collection.items.filter(item => item[0] === 'deposit').map(item => item[1].id)));
    if (recipient) {
        data.append('client_id', recipient[0].id);
        if (recipient[1]) {
            data.append('contact_id', recipient[1].id);
        }
    }

    // 0.00 amount does not need a payment method, so dont even bother sending it, if one is set up
    if (collection.total !== 0.0) {
        paymentMethodAmounts
            .filter(paymentMethodAmount => (paymentMethodAmount.amount !== 0.0 && paymentMethodAmount.amount !== null) || paymentMethodAmount.paymentMethod !== null)
            .forEach((paymentMethodAmount, i) => {
                if (paymentMethodAmount.paymentMethod) {
                    data.append(`payment_method_amounts[${i}][0]`, paymentMethodAmount.paymentMethod);
                }
                data.append(`payment_method_amounts[${i}][1]`, paymentMethodAmount.amount ?? collection.total);
                if (paymentIntents[i]) {
                    data.append(`payment_method_amounts[${i}][2]`, paymentIntents[i]);
                }
            });
    }

    return data;
}

function tryOpenMailPopup(invoiceKey) {
    startLoadingIndicator();

    return new Promise(resolve => {
        const intervalHandler = setWaitingInterval(() => {
            return fetch('/invoice-pdf/' + invoiceKey + '/status', {
                method: 'GET',
            }).then(async (response) => {
                if (response.ok) {
                    const body = await response.json();
                    if (!body.pending) {
                        intervalHandler();

                        emailSendModal(
                            '/pdf-viewer/get-email-message',
                            {
                                documentType: HISTORY_EMAIL_TYPE_INVOICE,
                                documentKey: invoiceKey,
                            },
                            '/pdf-viewer/send-email',
                            '',
                            () => showFlashMessage(translate('frontend', 'CLIENT_EMIL_SUCCESS'))
                        );

                        stopLoadingIndicator();
                        resolve();
                    }
                } else {
                    resolve();
                    intervalHandler();
                    alert(translate('frontend', 'ERROR_INPROCESS'));
                    stopLoadingIndicator();
                }
            });
        }, 2000, true); // ~1s to sign the invoice, and ~1s to generate the PDF
    });
}
